import React from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useFetch } from "hooks";
import { path } from "ramda";
import moment from "moment";
import { get, post } from "utils/api";
import { getApiErrorMessage, formatPhone } from "utils/misc";
import { Spinner } from "components/ui";
import { errorMessage } from "actions/message";
import { FaFileExcelO } from "react-icons/lib/fa";
import { browserHistory } from "browser-history";
import { setPatientBatchIds } from "actions/batch";
import { createTab } from "actions/system-tray";

const formatDate = date => (date ? moment(date).format("MM/DD/YYYY") : null);

export default function IncompleteCompliancePatients({
  dateFrom,
  dateTo,
  companyId,
  category,
  categoryName,
  closeModal
}) {
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    response: patientList,
    fetchData: fetchPatientList,
    isFetching: fetchingPatientList
  } = useFetch({
    apiFn: () =>
      get(`compliance-dashboard/patients-by-category`, {
        Category: category,
        Value: categoryName,
        DateFrom: dateFrom,
        DateTo: dateTo,
        CompanyId: companyId
      }),
    defaultValue: null,
    transformError: path(["response", "body", "status"]),
    onError: error => {
      dispatch(
        errorMessage(
          `Category: ${categoryName} ERROR: ${getApiErrorMessage(error)}`
        )
      );
      closeModal();
    }
  });

  const {
    fetchData: exportPatientsByCategory,
    isFetching: exportingPatientsByCategory
  } = useFetch({
    apiFn: () =>
      post(`compliance-dashboard/export-patients-by-category`, {
        Category: category,
        Value: categoryName,
        DateFrom: dateFrom,
        DateTo: dateTo,
        CompanyId: companyId
      }),
    defaultValue: null,
    transformError: path(["response", "body", "status"]),
    onError: error => {
      dispatch(
        errorMessage(
          `Error when trying to export patients by category. ${getApiErrorMessage(
            error
          )}`
        )
      );
    }
  });

  async function initialData() {
    await fetchPatientList();
  }

  async function exportPatients() {
    const exportSuccessful = await exportPatientsByCategory();
    if (exportSuccessful) {
      browserHistory.push("/exports");
    }
  }

  React.useEffect(() => {
    initialData();
  }, []);

  const currentUnalteredRoute = history.location.pathname;

  return (
    <div className="special-fields" style={{ height: "100%" }}>
        <header className="page-header">
          <div className="page-header-title">
            <h1>
              <span className="page-title">
                {`Patients for the category "${categoryName}"`}
              </span>
            </h1>
          </div>
          <div>{exportingPatientsByCategory && <Spinner />}</div>
          <div
            title="Export patients to excel file"
            className="patient-list-export"
            style={
              exportingPatientsByCategory ||
              fetchingPatientList ||
              patientList?.length === 0
                ? { opacity: "0.5", pointerEvents: "none" }
                : undefined
            }
            onClick={() => {
              exportPatients();
            }}
          >
            <FaFileExcelO />
          </div>
        </header>

        <div style={{ overflowX: "scroll", height: "93%" }}>
          <div
            style={{
              display: "grid",
              gridGap: 10,
              alignItems: "left",
              height: "fit-content"
            }}
          >
            {!fetchingPatientList ? (
              patientList && patientList.length > 0 ? (
                <table className="large-12 ">
                  <thead>
                    <tr>
                      <th>Patient Name</th>
                      <th>Account Number</th>
                      <th>Date Of Birth</th>
                      <th>Insurance</th>
                      <th>Branch</th>
                      <th>Status</th>
                      <th>Status Date</th>
                      <th>Inactivated By</th>
                      <th>Inactivation Reason</th>
                      <th>Compliance Coach</th>
                      <th>Is Compliant</th>
                      <th>Date Compliance Met</th>
                      <th>Requires Compliance</th>
                      <th>Doctor</th>
                      <th>Doctor Phone Number</th>
                      <th>Setup Date</th>
                      <th>Days Since Setup</th>
                      <th>Pap Type</th>
                      <th>Contact Type</th>
                      <th>Latest Contact</th>
                      <th>Outreach Date</th>
                      <th>Outreach Cycle</th>
                      <th>Mask ID</th>
                      <th>HCPCS Mask</th>
                      <th>Description Mask</th>
                      <th>Machine ID</th>
                      <th>HCPCS Machine</th>
                      <th>Description Machine</th>
                      <th>Task Type</th>
                      <th>Task Assignee</th>
                      <th>Task Date of Creation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patientList.map(patient => (
                      <tr key={patient.PatientGuid}>
                        <td>
                          <span
                            onClick={() => {
                              history.replace(
                                `${currentUnalteredRoute}?selectedTab=Compliance Data&fromDashboard=true`
                              );
                              dispatch(
                                setPatientBatchIds(
                                  [patient.PatientGuid],
                                  [patient.PatientName]
                                )
                              );
                              dispatch(
                                createTab({
                                  type: "patients",
                                  key: "patients",
                                  label: "Patients (1)",
                                  meta: { ids: [patient.PatientGuid] }
                                })
                              );
                            }}
                            style={{
                              color: "#4281A6",
                              lineHeight: "inherit",
                              textDecoration: "none",
                              cursor: "pointer"
                            }}
                          >
                            {patient.PatientName}
                          </span>
                        </td>
                        <td>{patient.AccountNumber}</td>
                        <td>{formatDate(patient.DateOfBirth)}</td>
                        <td>{patient.Insurance}</td>
                        <td>{patient.BranchName}</td>
                        <td>{patient.Status}</td>
                        <td>{formatDate(patient.StatusDate)}</td>
                        <td>{patient.InactivatedBy}</td>
                        <td>{patient.InactivationReason}</td>
                        <td>{patient.ComplianceCoach}</td>
                        <td>{patient.IsCompliant}</td>
                        <td>{formatDate(patient.DateComplianceMet)}</td>
                        <td>{patient.InsuranceRequiresCompliance}</td>
                        <td>{patient.Physician}</td>
                        <td>{formatPhone(patient.DoctorPhoneNumber)}</td>
                        <td>{formatDate(patient.SetupDate)}</td>
                        <td>{patient.DaysSinceSetup}</td>
                        <td>{patient.PapType}</td>
                        <td>{patient.ContactType}</td>
                        <td>{formatDate(patient.LatestContact)}</td>
                        <td>{formatDate(patient.OutreachDate)}</td>
                        <td>{patient.OutreachCycle}</td>
                        <td>{patient.MaskID}</td>
                        <td>{patient.HCPCSMask}</td>
                        <td>{patient.DescriptionMask}</td>
                        <td>{patient.MachineID}</td>
                        <td>{patient.HCPCSMachine}</td>
                        <td>{patient.DescriptionMachine}</td>
                        <td>{patient.TaskType}</td>
                        <td>{patient.TaskAssignee}</td>
                        <td>{formatDate(patient.TaskDateOfCreation)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No records found for applied filters</p>
              )
            ) : (
              <Spinner />
            )}
          </div>
        </div>
      </div>
  );
}

IncompleteCompliancePatients.propTypes = {
  categoryName: PropTypes.string.isRequired,
  category: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  closeModal: PropTypes.func.isRequired,
  dateFrom: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  dateTo: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  companyId: PropTypes.string
};
